<template>
  <div class="universal-search" :data-status="expansionStatus">
    <div class="universal-search-bar">
      <button v-if="expansionStatus == 'expanded'" class="icon-button" :title="__('Go back')" @click="collapse">
        <base-icon class="back-icon" name="arrow_back" />
      </button>
      <div v-else class="universal-search-icon" aria-hidden="true" @click="expand">
        <base-icon name="search" />
      </div>
      <form class="universal-search-form" @submit.prevent="search">
        <input
          ref="searchField"
          class="universal-search-input"
          type="search"
          name="search"
          :placeholder="__('Search')"
          :value="query"
          :title="__('Search')"
          @input="search"
          @focus="expand"
          @keyup.esc="collapse"
        />
        <button
          v-if="expansionStatus == 'expanded'"
          type="submit"
          class="raised-button universal-search-submit responsive-no-touch-only"
        >
          {{ __('Search') }}
        </button>
        <button
          v-if="expansionStatus == 'expanded'"
          class="icon-button universal-search-reset responsive-touch-only"
          :aria-label="__('Clear')"
          @click="resetSearch"
        >
          <base-icon name="cancel" />
        </button>
      </form>
      <div></div>
    </div>
    <div v-if="expansionStatus == 'expanded'" class="universal-search-results">
      <div v-if="working" class="progress progress-hanging"><div class="progress-indeterminate"></div></div>
      <div v-if="walls.length > 0" class="container">
        <section class="padlet-cards-section">
          <padlet-collection :walls="walls"></padlet-collection>
        </section>
      </div>
      <div v-else-if="isEmpty" class="universal-search-no-result caption-type container">{{ emptyResultsMessage }}</div>
    </div>
  </div>
</template>

<script>
import BaseIcon from 'vuecomponents/base_icon'
import PadletCollection from 'vuecomponents/padlet_collection'
import { __ } from 'bits/intl'
import device from 'bits/device'
import { fetchJson } from '@padlet/fetch'
import { trackEvent } from 'bits/analytics'
import debounce from 'bits/debounce'

const debouncedWallSearch = debounce(function (query, prelude, thenHandler) {
  prelude()
  return fetchJson('/search/padlets.json', {
    query: {
      q: query,
    },
  }).then(thenHandler)
}, 500)

export default {
  components: {
    PadletCollection: PadletCollection,
    BaseIcon: BaseIcon,
  },
  props: {
    status: {
      default: 'collapsed',
    },
  },
  data: function () {
    return {
      working: false,
      previousQuery: '',
      searchResults: [],
      recents: [],
      expansionStatus: this.status,
      query: '',
      emptyResultsMessage: __('Nothing matches your search query.'),
    }
  },
  computed: {
    walls: function () {
      if (this.query === '') {
        return this.recents
      } else {
        return this.searchResults
      }
    },
    isEmpty: function () {
      return this.query && !this.working
    },
  },
  methods: {
    makeSearchCall: function () {
      let prelude, self
      self = this
      debouncedWallSearch.cancel()
      prelude = function () {
        return (self.working = true)
      }
      return debouncedWallSearch(self.query, prelude, function (data) {
        self.working = false
        return (self.searchResults = data.hits)
      })
    },
    //fetchJson("/search/padlets.json",
    //  data:
    //    q: self.query
    //).then((data) ->
    //  self.working = false
    //  self.searchResults = data.hits
    //)
    makeRecentsCall: function () {
      let self
      self = this
      self.working = true
      return fetchJson('/walls', {
        data: {
          filter: 'recents',
        },
      }).then(function (data) {
        self.working = false
        return (self.recents = data)
      })
    },
    collapse: function (e) {
      this.expansionStatus = 'collapsed'
      this.$refs.searchField.blur()
      this.query = ''
      this.previousQuery = ''
      return (this.searchResults = [])
    },
    expand: function (e) {
      if (this.expansionStatus !== 'expanded') {
        trackEvent('Universal Search', 'Open')
        this.makeRecentsCall()
        this.expansionStatus = 'expanded'
        return this.$refs.searchField.focus()
      }
    },
    resetSearch: function (e) {
      this.$refs.searchField.value = ''
      this.$refs.searchField.focus()
      return this.search()
    },
    search: function (e) {
      this.query = this.$refs.searchField.value
      if (this.query === '') {
        return this.makeRecentsCall()
      } else {
        if (this.previousQuery !== this.query) {
          this.previousQuery = this.query
          return this.makeSearchCall()
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';

.universal-search[data-status='expanded'] {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: $default-background-color;
  display: flex;
  flex-direction: column;
  z-index: 1;

  .universal-search-bar {
    background-color: white;
    height: 56px;
    flex-shrink: 0;
    @include shadow-z1;
    z-index: 1; //have to do this or else results overlap;
    animation-name: swipe-from-top;
    animation-duration: 0.2s;
  }

  .universal-search-input {
    border-radius: $default-border-radius;
    background-color: $grey-2;
    @include directional-padding($padding-left: 8px, $padding-right: 4px);
    @include directional-margin($margin-top: 0, $margin-right: 8px, $margin-bottom: 0, $margin-left: 0);
  }
}

.universal-search[data-status='collapsed'] {
  .universal-search-bar {
    background-color: $default-background-color;
    border-radius: 16px;
    height: 32px;
    margin: 0 8px;
  }
}

.universal-search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
}

.universal-search-form {
  display: flex;
  align-items: center;
  flex-grow: 1;
  max-width: 1024px;
}

.universal-search-submit {
  flex-shrink: 0;
}

.universal-search-input {
  box-shadow: none;
  margin-bottom: 0;
  flex-grow: 1;

  &:focus,
  &:active {
    box-shadow: none;
  }
}

.universal-search-reset i {
  color: var(--muted-text-color);
}

.universal-search-icon {
  padding: 4px 8px;
  color: $muted-text-color;
  display: flex;
  align-items: center;
}

.universal-search-results {
  position: relative;
  flex-grow: 1;
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  .container {
    margin-top: 32px;
  }
}

.padlet-cards-list,
.universal-search-no-result {
  margin-top: 48px;
}

.universal-search-no-result,
.empty-message {
  text-align: center;
}

.empty-message {
  padding: 0px 24px;
}
</style>
